/**
 * AIストラテジー - ベース（ランダム）
 *
 * すべての判断をランダムで行うベースストラテジー。
 * 各メソッドをオーバーライドすることで判断基準をカスタマイズ可能。
 */

import type { MonsterInstance, CardData, CardWithId, PhaseCardInstance, HandCard } from '../../../types/card';
import type { StatusEffect } from '../../../types/effect';
import type { PlayerNumber } from '../../../types/player';
import type { DeepSeaZone, DeepSeaCard } from '../../deepSeaZone/types';
import { getCounterAttackMultiplier } from '../../keywordAbilities';

/** AIゲーム状態 */
export interface AIGameState {
  currentPlayer: PlayerNumber;
  myField: (MonsterInstance | null)[];
  opponentField: (MonsterInstance | null)[];
  myHand: CardWithId[];
  opponentHandCount: number;
  myLife: number;
  opponentLife: number;
  myActiveSP: number;
  myRestedSP: number;
  myBonusSP?: number;
  myDeckCount: number;
  opponentDeckCount: number;
  myDeck: CardWithId[];
  opponentDeck: CardWithId[];
  myGraveyard: CardWithId[];
  opponentGraveyard: CardWithId[];
  myFieldCard: CardData | null;
  opponentFieldCard: CardData | null;
  myPhaseCard: PhaseCardInstance | null;
  opponentPhaseCard: PhaseCardInstance | null;
  myStatusEffects: StatusEffect[];
  turn: number;
  phase: number;
  isFirstTurn: boolean;
  firstPlayer?: PlayerNumber;
  // 深海領域
  myDeepSeaZone: DeepSeaZone | null;
  opponentDeepSeaZone: DeepSeaZone | null;
}

/** 使用可能なスキル情報 */
export interface UsableSkill {
  monsterIndex: number;
  skillType: 'basic' | 'advanced';
  monster: MonsterInstance;
}

/** チャージ可能なモンスター情報 */
export interface ChargeableMonster {
  monsterIndex: number;
  monster: MonsterInstance;
  currentCharges: number;
}

/** デッキ確認オプション */
export interface DeckReviewOptions {
  allowReorder?: boolean;
  selectMode?: boolean;
}

/** 残りアクション情報 */
export interface ActionsRemaining {
  canSummon: boolean;
  canUseSkill: boolean;
  canUseMagic: boolean;
  canCharge: boolean;
  canChargePhaseCard: boolean;
  canPlaceFieldCard: boolean;
  canPlacePhaseCard: boolean;
  canActivateTrigger: boolean;
  // 深海領域アクション
  canSubmerge: boolean;
  canSurface: boolean;
  summonableCards: CardWithId[];
  usableSkills: UsableSkill[];
  usableMagicCards: CardWithId[];
  chargeableMonsters: ChargeableMonster[];
  chargeableCards: CardWithId[];
  placeableFieldCards: CardWithId[];
  placeablePhaseCards: CardWithId[];
  phaseCardChargeableCards: CardWithId[];
  // 深海領域カード
  submergibleCards: HandCard[];
  surfaceableCards: DeepSeaCard[];
}

/** チェインコンテキスト（刹那詠唱発動時の状況） */
export interface ChainContext {
  type?: 'battleStart' | 'attackDeclaration' | 'other';
  attacker?: MonsterInstance;
  target?: MonsterInstance | null;
  [key: string]: unknown;
}

/** AIストラテジーインターフェース */
export interface AIStrategy {
  chooseSummon: (summonableCards: CardWithId[], gameState: AIGameState) => CardWithId | null;
  chooseSlot: (emptySlots: number[], card: CardWithId, gameState: AIGameState) => number | null;
  chooseSkill: (usableSkills: UsableSkill[], gameState: AIGameState) => UsableSkill | null;
  chooseCharge: (chargeableCards: CardWithId[], chargeableMonsters: ChargeableMonster[], gameState: AIGameState) => { card: CardWithId; monsterIndex: number } | null;
  chooseTrigger: (activatableTriggers: unknown[], gameState: AIGameState) => unknown | null;
  chooseAttackOrder: (attackableIndices: number[], gameState: AIGameState) => number[];
  chooseAttackTarget: (validTargets: (number | 'direct')[], attacker: MonsterInstance, gameState: AIGameState) => number | 'direct';
  chooseFromHand: (validCards: CardWithId[], message: string, gameState: AIGameState) => CardWithId | null;
  chooseMonsterTarget: (validIndices: number[], message: string, gameState: AIGameState) => number | null;
  chooseFromGraveyard: (validCards: CardWithId[], message: string, gameState: AIGameState) => CardWithId | null;
  chooseFromDeckReview: (cards: CardWithId[], options: DeckReviewOptions, gameState: AIGameState) => { selectedCards?: CardWithId[]; remainingCards?: CardWithId[]; reorderedCards?: CardWithId[] };
  chooseSetsuna: (availableSetsunaCards: CardWithId[], chainContext: ChainContext, gameState: AIGameState) => CardWithId | null;
  chooseMagicCard: (usableMagicCards: CardWithId[], gameState: AIGameState) => CardWithId | null;
  chooseFieldCard: (placeableFieldCards: CardWithId[], gameState: AIGameState) => CardWithId | null;
  choosePhaseCard: (placeablePhaseCards: CardWithId[], gameState: AIGameState) => CardWithId | null;
  choosePhaseCardCharge: (phaseCardChargeableCards: CardWithId[], gameState: AIGameState) => CardWithId | null;
  // 深海領域メソッド
  chooseSubmerge: (submergibleCards: HandCard[], gameState: AIGameState) => HandCard | null;
  chooseSurface: (surfaceableCards: DeepSeaCard[], gameState: AIGameState) => { deepSeaCard: DeepSeaCard; fieldSlot: number } | null;
  shouldEndMainPhase: (gameState: AIGameState, actionsRemaining: ActionsRemaining) => boolean;
  shouldEndBattlePhase: (gameState: AIGameState, remainingAttackers: number[]) => boolean;
}

// ユーティリティ関数
export function randomPick<T>(array: T[]): T | null {
  if (!array || array.length === 0) return null;
  return array[Math.floor(Math.random() * array.length)];
}

export function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * 攻撃スキップ判断
 * 反撃で死ぬ場合に攻撃すべきかどうかを判断する
 *
 * 飛行システム対応:
 * - 飛行 → 地上: 反撃なし（0%）
 * - 地上 → 地上: 通常反撃（30%）
 * - 飛行 → 飛行: 通常反撃（30%）
 * - 地上 → 飛行: 通常反撃（30%）※対空なら攻撃可能
 */
export function shouldSkipAttackOnMonster(
  attacker: MonsterInstance | null,
  target: MonsterInstance | null,
  gameState: AIGameState
): boolean {
  if (!attacker || !target) return false;

  const myAtk = attacker.currentAttack || attacker.attack || 0;
  const myHp = attacker.currentHp || attacker.hp || 0;
  const targetAtk = target.currentAttack || target.attack || 0;
  const targetHp = target.currentHp || target.hp || 0;

  // 飛行システム対応: 反撃ダメージ倍率を取得
  // 飛行 → 地上: 0% / それ以外: 30%
  const counterMultiplier = getCounterAttackMultiplier(attacker, target);
  const counterDamage = Math.floor(targetAtk * counterMultiplier);

  // 反撃なし → 攻撃する（飛行→地上など）
  if (counterMultiplier === 0) return false;

  // 反撃で死ぬかどうか
  const willDieFromCounter = counterDamage >= myHp;

  // 反撃で死なない → 攻撃する
  if (!willDieFromCounter) return false;

  // 相手を倒せるか
  const canKillTarget = myAtk >= targetHp;

  // 相手を倒せる → 攻撃する（1:1トレード）
  if (canKillTarget) return false;

  // ここから「反撃で死ぬ AND 相手を倒せない」ケース

  // 技があるかどうか
  const hasSkill = !!(attacker.basicSkill || attacker.advancedSkill);

  // 最後の1体かどうか
  const myMonsterCount = gameState.myField.filter(m => m).length;
  const isLastMonster = myMonsterCount === 1;

  // 技がある → 攻撃しない（温存）
  if (hasSkill) return true;

  // 最後の壁 → 攻撃しない（温存）
  if (isLastMonster) return true;

  // それ以外 → 攻撃する（少しでもダメージを与える）
  return false;
}

/**
 * ベースストラテジー（すべてランダム）
 */
export const baseStrategy: AIStrategy = {
  /** 召喚するカードを選択 */
  chooseSummon(summonableCards: CardWithId[], gameState: AIGameState): CardWithId | null {
    if (summonableCards.length === 0) return null;
    return randomPick(summonableCards);
  },

  /** 召喚先スロットを選択 */
  chooseSlot(emptySlots: number[], card: CardWithId, gameState: AIGameState): number | null {
    if (emptySlots.length === 0) return null;
    return randomPick(emptySlots);
  },

  /** 使用するスキルを選択 */
  chooseSkill(usableSkills: UsableSkill[], gameState: AIGameState): UsableSkill | null {
    if (usableSkills.length === 0) return null;
    // デフォルト: スキル使用しない（チャージを温存）
    return null;
  },

  /** チャージするカードとモンスターを選択 */
  chooseCharge(chargeableCards: CardWithId[], chargeableMonsters: ChargeableMonster[], gameState: AIGameState): { card: CardWithId; monsterIndex: number } | null {
    // デフォルト: チャージしない
    return null;
  },

  /** 発動するトリガーを選択 */
  chooseTrigger(activatableTriggers: unknown[], gameState: AIGameState): unknown | null {
    if (activatableTriggers.length === 0) return null;
    // デフォルト: 発動しない
    return null;
  },

  /** 攻撃するモンスターと攻撃順序を決定 */
  chooseAttackOrder(attackableIndices: number[], gameState: AIGameState): number[] {
    // デフォルト: ランダム順で全員攻撃
    return shuffleArray([...attackableIndices]);
  },

  /** 攻撃対象を選択 */
  chooseAttackTarget(validTargets: (number | 'direct')[], attacker: MonsterInstance, gameState: AIGameState): number | 'direct' {
    if (validTargets.length === 0) return 'direct';
    return randomPick(validTargets) ?? 'direct';
  },

  /** 手札選択（効果による選択） */
  chooseFromHand(validCards: CardWithId[], message: string, gameState: AIGameState): CardWithId | null {
    if (validCards.length === 0) return null;
    return randomPick(validCards);
  },

  /** モンスターターゲット選択（効果による選択） */
  chooseMonsterTarget(validIndices: number[], message: string, gameState: AIGameState): number | null {
    if (validIndices.length === 0) return null;
    return randomPick(validIndices);
  },

  /** 墓地カード選択（効果による選択） */
  chooseFromGraveyard(validCards: CardWithId[], message: string, gameState: AIGameState): CardWithId | null {
    if (validCards.length === 0) return null;

    // コストが高いカードを優先（同コストなら攻撃力が高い方）
    const sorted = [...validCards].sort((a, b) => {
      const costDiff = (b.cost || 0) - (a.cost || 0);
      if (costDiff !== 0) return costDiff;
      return (b.attack || 0) - (a.attack || 0);
    });
    return sorted[0];
  },

  /** デッキ確認時の選択（効果による選択） */
  chooseFromDeckReview(cards: CardWithId[], options: DeckReviewOptions, gameState: AIGameState): { selectedCards?: CardWithId[]; remainingCards?: CardWithId[]; reorderedCards?: CardWithId[] } {
    if (options.selectMode && cards.length > 0) {
      // コストが高いカードを優先（同コストなら攻撃力が高い方）
      const sorted = [...cards].sort((a, b) => {
        const costDiff = (b.cost || 0) - (a.cost || 0);
        if (costDiff !== 0) return costDiff;
        return (b.attack || 0) - (a.attack || 0);
      });
      const selectedCard = sorted[0];
      const selectedCards = [selectedCard];
      const remainingCards = cards.filter(c => c.uniqueId !== selectedCard.uniqueId);
      return { selectedCards, remainingCards };
    }
    // 並び替えはそのまま
    return { reorderedCards: cards };
  },

  /** 刹那詠唱を発動するか判断 */
  chooseSetsuna(availableSetsunaCards: CardWithId[], chainContext: unknown, gameState: AIGameState): CardWithId | null {
    // デフォルト: 発動しない
    return null;
  },

  /** 魔法カードを使用するか判断 */
  chooseMagicCard(usableMagicCards: CardWithId[], gameState: AIGameState): CardWithId | null {
    // デフォルト: 使用しない
    return null;
  },

  /** フィールドカードを配置するか判断 */
  chooseFieldCard(placeableFieldCards: CardWithId[], gameState: AIGameState): CardWithId | null {
    // デフォルト: 配置しない
    return null;
  },

  /** フェイズカードを配置するか判断 */
  choosePhaseCard(placeablePhaseCards: CardWithId[], gameState: AIGameState): CardWithId | null {
    // デフォルト: 配置しない
    return null;
  },

  /** フェイズカードにチャージするカードを選択 */
  choosePhaseCardCharge(phaseCardChargeableCards: CardWithId[], gameState: AIGameState): CardWithId | null {
    // デフォルト: チャージしない
    return null;
  },

  /** 潜航するカードを選択（深海領域） */
  chooseSubmerge(submergibleCards: HandCard[], gameState: AIGameState): HandCard | null {
    // デフォルト: 潜航しない
    return null;
  },

  /** 浮上するカードとスロットを選択（深海領域） */
  chooseSurface(surfaceableCards: DeepSeaCard[], gameState: AIGameState): { deepSeaCard: DeepSeaCard; fieldSlot: number } | null {
    // デフォルト: 浮上しない
    return null;
  },

  /** メインフェイズを終了するか判断 */
  shouldEndMainPhase(gameState: AIGameState, actionsRemaining: ActionsRemaining): boolean {
    // デフォルト: アクションがなければ終了
    const { canSummon, canUseSkill, canUseMagic, canActivateTrigger } = actionsRemaining;
    return !canSummon && !canUseSkill && !canUseMagic && !canActivateTrigger;
  },

  /** バトルフェイズを終了するか判断 */
  shouldEndBattlePhase(gameState: AIGameState, remainingAttackers: number[]): boolean {
    // デフォルト: 全員攻撃したら終了
    return remainingAttackers.length === 0;
  },
};
